"use client";
import React, { useEffect, useState } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "../context/AuthContext";
import { BarChart3 } from "lucide-react";

export default function ReportsTable() {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const REPORTS_ENDPOINT = `${BASE_URL}/reports`;

  useEffect(() => {
    if (!token) return;

    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(REPORTS_ENDPOINT, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to fetch reports");

        const json = await res.json();
        setReports(json.data ?? []);
      } catch (err) {
        setError(err.message || "Something went wrong");
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [token]);

  const getPriorityStyle = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-orange-50 text-orange-600";
      case "medium":
        return "bg-yellow-50 text-yellow-700";
      case "low":
        return "bg-teal-50 text-teal-700";
      default:
        return "bg-red-100 text-red-600";
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading reports...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center shadow-sm">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>

     <div className="mb-6">
  <h1 className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight">
   Suggestions & Issues
  </h1>
  <p className="mt-1 text-gray-600">
    Manage and view system Suggestions & Issues
  </p>
</div>
      </div>

      {/* ------------------ MOBILE VIEW ------------------ */}
      <div className="md:hidden space-y-3">
        {reports.length > 0 ? (
          reports.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
            >
              <div className="flex justify-between items-start mb-2 gap-2">
                <h2 className="font-semibold text-gray-800 text-sm">
                  #{r.id} • {r.title}
                </h2>

                <span
                  className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${getPriorityStyle(
                    r.priority_level
                  )}`}
                >
                  {r.priority_level}
                </span>
              </div>

              <p className="text-sm text-gray-600">
                <span className="font-medium">Subject:</span>{" "}
                {r.subject}
              </p>

              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span>{" "}
                {r.email}
              </p>

              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Description:</span>{" "}
                {r.description}
              </p>

              {r.attachment && (
                <a
                  href={`${CDN_URL}/${r.attachment}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm text-teal-600 hover:underline"
                >
                  View Document
                </a>
              )}

              <p className="text-xs text-gray-400 mt-2">
                {r.create_audit_time}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 py-8">
            No reports found
          </div>
        )}
      </div>

      {/* ------------------ TABLE VIEW ------------------ */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Priority</th>

                <th className="px-4 py-3 hidden lg:table-cell">
                  Description
                </th>

                <th className="px-4 py-3 hidden lg:table-cell">
                  Email
                </th>

                <th className="px-4 py-3">Attachment</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>

            <tbody>
              {reports.length > 0 ? (
                reports.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="px-4 py-3">{r.id}</td>
                    <td className="px-4 py-3">{r.title}</td>

                    <td className="px-4 py-3 max-w-xs whitespace-normal">
                      {r.subject}
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getPriorityStyle(
                          r.priority_level
                        )}`}
                      >
                        {r.priority_level}
                      </span>
                    </td>

                    <td className="px-4 py-3 hidden lg:table-cell max-w-md whitespace-normal">
                      {r.description}
                    </td>

                    <td className="px-4 py-3 hidden lg:table-cell">
                      {r.email}
                    </td>

                    <td className="px-4 py-3">
                      {r.attachment ? (
                        <a
                          href={`${CDN_URL}/${r.attachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          Document
                        </a>
                      ) : (
                        <span className="text-gray-400">
                          No file
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {r.create_audit_time}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No reports found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}